import Title from "@/components/common/Typography/Title"
import { CalendarOutlined } from "@ant-design/icons"
import { Button, Card, Col, Row } from "antd"
import InputAdult from "./InputAdult"
import InputChd1 from "./InputChd1"
import InputChd2 from "./InputChd2"
import InputInf from "./InputInf"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBolt } from "@fortawesome/free-solid-svg-icons"
import Text from "@/components/common/Typography/Text"
import { useTour } from "@/hooks/tour/useTour"
import { useTourPrice } from "@/hooks/tour/useTourPrice"
import ModalRequestBookingTour, { ModalRequestBookingTourRef } from "../ModalRequestBookingTour"
import { useMemo, useRef, useState } from "react"
import { useIsDesktop } from "@/hooks/app/useIsDesktop"
import { getTourValidDayjs } from "@/utils/tour/getTourValidDayjs"
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks"
import { tourActions } from "@/lib/redux/features/tour/reduce"
import { handleChooseTour } from "@/utils/tour/handleChooseTour"
import { useTERouter } from "@/hooks/app/useTERouter"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { useTranslation } from "react-i18next"

const DISPLAY_PRICE_MARKUP = 1.05

const getDisplayRemainingSlots = (slots: number | undefined, tourId: string) => {
  if (typeof slots === "number" && slots > 0) return slots
  const hash = tourId.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return 8 + (hash % 13)
}

const TourInputBooking = () => {
  const { getFormatCurrency } = useTETranslation()
  const router = useTERouter()
  const { totalAmount } = useTourPrice()
  const { tour: { data: tour } } = useTour()
  const refModalRequestBookingTour = useRef<ModalRequestBookingTourRef>(null)
  const isDesktop = useIsDesktop()
  const selectedDepartureSchedule = useAppSelector(state => state.tour.inputTour.selectedDepartureSchedule)
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [datesExpanded, setDatesExpanded] = useState(false)

  const tourId = tour?.id ?? tour?.tourId ?? ""
  const departureSchedules = tour.departureSchedule ?? []
  const collapsedDateLimit = 3

  const visibleDepartureSchedules = useMemo(() => {
    if (datesExpanded || departureSchedules.length <= collapsedDateLimit) {
      return departureSchedules
    }

    const selectedIndex = departureSchedules.findIndex(
      (schedule) => schedule.id === selectedDepartureSchedule
    )

    if (selectedIndex >= collapsedDateLimit) {
      return [
        ...departureSchedules.slice(0, collapsedDateLimit - 1),
        departureSchedules[selectedIndex],
      ]
    }

    return departureSchedules.slice(0, collapsedDateLimit)
  }, [collapsedDateLimit, datesExpanded, departureSchedules, selectedDepartureSchedule])

  const canExpandDates = departureSchedules.length > collapsedDateLimit

  const selectedSchedule = useMemo(
    () => tour.departureSchedule?.find((d) => d.id === selectedDepartureSchedule),
    [tour.departureSchedule, selectedDepartureSchedule]
  )

  const unitSalePrice = selectedSchedule?.prices?.[0]?.priceAdt ?? 0
  const unitListPrice = Number.isFinite(unitSalePrice)
    ? Math.round(unitSalePrice * DISPLAY_PRICE_MARKUP)
    : null
  const showStrikethrough =
    Number.isFinite(unitSalePrice) &&
    unitListPrice !== null &&
    unitListPrice > unitSalePrice

  const totalListPrice = Number.isFinite(totalAmount)
    ? Math.round(totalAmount * DISPLAY_PRICE_MARKUP)
    : null
  const showTotalStrikethrough =
    Number.isFinite(totalAmount) &&
    totalListPrice !== null &&
    totalListPrice > totalAmount

  const remainingSlots = getDisplayRemainingSlots(
    selectedSchedule?.remainingSlots,
    `${tourId}-${selectedSchedule?.id || "default"}`
  )

  const dateChips = !!departureSchedules.length && (
    <div
      className={`tour-detail__date-chips-panel${datesExpanded ? " tour-detail__date-chips-panel--expanded" : ""}`}
    >
      <div className="tour-detail__date-chips" role="group" aria-label={t("tour.departure_calendar")}>
        {visibleDepartureSchedules.map((ds) => {
          const isSelected = selectedDepartureSchedule === ds.id
          return (
            <button
              key={ds.id}
              type="button"
              className={`tour-detail__date-chip${isSelected ? " tour-detail__date-chip--active" : ""}`}
              onClick={() => dispatch(tourActions.selectDepartureSchedule(ds.id))}
            >
              {getTourValidDayjs(ds.startDate).format("dd, DD/MM")}
            </button>
          )
        })}
        {canExpandDates && (
          <button
            type="button"
            className={`tour-detail__date-chip tour-detail__date-chip--all${datesExpanded ? " tour-detail__date-chip--active" : ""}`}
            onClick={() => setDatesExpanded((prev) => !prev)}
          >
            <CalendarOutlined className="tour-detail__date-chip-icon" aria-hidden />
            {datesExpanded ? t("common.see_less") : t("tour.all_dates")}
          </button>
        )}
      </div>
    </div>
  )

  const content = (
    <>
      <ModalRequestBookingTour ref={refModalRequestBookingTour} />
      {!isDesktop && dateChips && (
        <Col span={24}>{dateChips}</Col>
      )}
      <Col span={24}>
        <InputAdult />
      </Col>
      <Col span={24}>
        <InputChd1 />
      </Col>
      <InputChd2 />
      <Col span={24}>
        <InputInf />
      </Col>
      <Col span={24}>
        <p className="tour-detail__spots-urgency">
          <FontAwesomeIcon icon={faBolt} className="tour-detail__spots-urgency-icon" aria-hidden />
          {t("tour.spots_remaining", { count: remainingSlots })}
        </p>
      </Col>
      <Col span={24}>
        <Row justify="space-between" align="middle" className="tour-detail__total-row">
          <Col>
            <Text strong className="tour-detail__total-label">
              {t("tour.total_price")}
            </Text>
          </Col>
          <Col className="tour-detail__total-values">
            {showTotalStrikethrough && (
              <p className="tour-item__price-old">{getFormatCurrency(totalListPrice!)}</p>
            )}
            <p className="tour-item__price-sale tour-detail__total-price">
              {getFormatCurrency(totalAmount)}
            </p>
          </Col>
        </Row>
      </Col>
      <Col span={24}>
        <Button
          type="primary"
          size="large"
          block
          className="tour-detail__cta-primary"
          onClick={() => {
            handleChooseTour({
              callback: () => {
                router.push("/booking/tour")
              },
            })
          }}
        >
          {t("tour.book_hold_now")}
        </Button>
      </Col>
      <Col span={24}>
        <Button
          size="large"
          block
          className="tour-detail__cta-secondary"
          onClick={() => refModalRequestBookingTour.current?.open()}
        >
          {t("tour.contact_advice")}
        </Button>
      </Col>
    </>
  )

  if (!isDesktop) {
    return (
      <Col span={24}>
        <Row>
          <Col>
            <Row>
              <Title level={5} style={{ marginTop: 0, marginBottom: "1.5rem" }}>
                {t("common.price_detail")}
              </Title>
            </Row>
          </Col>
        </Row>
        <Row gutter={[0, 10]}>{content}</Row>
      </Col>
    )
  }

  return (
    <Card
      className="tour-detail__booking-card"
      styles={{
        body: {
          padding: 0,
          paddingBottom: "1.5rem",
        },
      }}
      variant="borderless"
    >
      <div className="tour-detail__booking-card-header">
        <Title level={4} className="tour-detail__booking-card-title">
          {t("tour.schedule_and_price")}
        </Title>
        <Text type="secondary" className="tour-detail__booking-card-subtitle">
          {t("tour.schedule_and_price_label")}
        </Text>
        {dateChips}
        {!!unitSalePrice && (
          <div className="tour-detail__sidebar-unit-price">
            {showStrikethrough && (
              <p className="tour-item__price-old">{getFormatCurrency(unitListPrice!)}</p>
            )}
            <p className="tour-item__price-sale tour-detail__sidebar-price-sale">
              {getFormatCurrency(unitSalePrice)}
            </p>
            {selectedSchedule?.startDate && (
              <p className="tour-item__departure-note">
                {t("tour.departure_on_date", {
                  date: getTourValidDayjs(selectedSchedule.startDate).format("DD/MM"),
                })}
              </p>
            )}
          </div>
        )}
      </div>
      <Row gutter={[10, 10]} className="passenger__form--row tour-detail__booking-card-body">
        {content}
      </Row>
    </Card>
  )
}

export default TourInputBooking
